'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {breakpoint, mb, mr, p} from '@nib/layout';
import Heading, {validComponentValues, componentValues} from '@nib-components/heading';
import {formBackgroundColor, copyColor, formLabelColor} from '@nib-components/theme';

const validFormModes = ['light', 'white'] as const;
type FormModes = (typeof validFormModes)[number];

type Nullable<T> = T | null;

interface FormProps {
  id: string;
  name: string;
  title?: string;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  action?: string;
  containerWidth?: Nullable<string>;
  spaceChildren?: boolean;
  titleComponent?: componentValues;
  formMode?: FormModes;
  isCompact?: boolean;
  children?: React.ReactNode;
  [key: string]: unknown;
}

export const StyledHeading = styled(Heading)`
  color: ${formLabelColor};
`;

export const FormInput = styled.form<FormProps>`
  box-sizing: border-box;
  ${p(4)};

  ${breakpoint('sm')`
    ${p(6)};
  `}

  background-color: ${formBackgroundColor};
  color: ${copyColor};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const FormContainer = styled(({containerWidth, isCompact, spaceChildren, ...rest}) => <div {...rest} />)`
  ${({containerWidth}) => containerWidth && `max-width: ${containerWidth};`};
  margin-left: auto;
  margin-right: auto;

  ${({isCompact}) =>
    isCompact &&
    `
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
  `};

  ${({isCompact, spaceChildren}) =>
    isCompact &&
    spaceChildren &&
    css`
      > * {
        ${mr(4)};
        ${mb(5)};
      }
    `};

  ${({spaceChildren, isCompact}) =>
    spaceChildren &&
    !isCompact &&
    css`
      > * {
        ${mb(7)};
      }
    `};
`;

const Form: React.FC<FormProps> = props => {
  const {title, children, containerWidth, spaceChildren, formMode, isCompact, titleComponent = 'h3', onSubmit, ...other} = props;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!props.action) {
      event.preventDefault();
    }

    // TODO: This is to prevent a breaking change. Should be removed in the next major version
    onSubmit && onSubmit(event);
  };

  return (
    <FormInput {...other} onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e)} formMode={formMode}>
      <FormContainer containerWidth={containerWidth} spaceChildren={spaceChildren} isCompact={isCompact}>
        {title ? (
          <StyledHeading size={2} component={titleComponent}>
            {title}
          </StyledHeading>
        ) : null}
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              isCompact
            });
          }
          return child;
        })}
      </FormContainer>
    </FormInput>
  );
};

Form.displayName = 'Form';

Form.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  onSubmit: PropTypes.func,
  action: PropTypes.string,
  containerWidth: PropTypes.string,
  spaceChildren: PropTypes.bool,
  formMode: PropTypes.oneOf(validFormModes),
  isCompact: PropTypes.bool,
  titleComponent: PropTypes.oneOf(validComponentValues),
  children: PropTypes.node
};

Form.defaultProps = {
  title: '',
  containerWidth: '768px',
  spaceChildren: true,
  isCompact: false,
  titleComponent: 'h3',
  onSubmit: () => {}
};

export default Form;
